import React, { createContext, useCallback, useMemo, useState } from "react";

import { OibInformation } from "../../model/OibType";
import { UploadInformation } from "../../model/UploadType";
import { PersonalInformation, PersonalInformationCallback } from "../../model/PersonalInformationType";
import { FinancialInformation, FinancialInformationCallback } from "../../model/FinancialInformationType";
import {
    TaxResidencyCountryInformation,
    TaxResidencyInformation,
    TaxResidencyInformationCallback
} from "../../model/TaxResidencyType";
import { PoliticalExposureInformation, PoliticalExposureInformationCallback } from "../../model/PoliticalExposureType";
import {
    DocumentationDeliveryInformation,
    DocumentationDeliveryInformationCallback
} from "../../model/DocumentationDeliveryType";
import {
    CreateZahtjevResponse,
    DrzavaOporezivanja,
    useGetZahtjev,
    ZahtjevResponseDostavaDokumentacije,
    ZahtjevResponseFinancijskeInformacije,
    ZahtjevResponseMarketinskaPrivola,
    ZahtjevResponseOsobniPodaci,
    ZahtjevResponsePoreznaRezidentnost
} from "../../api/RegistrationApi";
import { useNavigate } from "react-router-dom";
import {
    ACCESS_TOKEN_KEY,
    ANALYTIC_USER_ID,
    COOKIE_PREFERENCES_KEY,
    UPLOAD_HASH_KEY,
    UPLOAD_ID_KEY,
    ZAHTJEV_TOKEN_KEY
} from "../constants/persistentDataConstants";
import { ERROR_MESSAGE_NOTIFICATION } from "../constants/notificationConstants";
import { useNotification } from "./NotificationProvider";
import { useDialog } from "./DialogProvider";
import {
    CONTACT_ADDRESS_STATUS,
    DOCUMENTATION_DELIVERY_INFORMATION_STATUS,
    FINANCIAL_INFORMATION_STATUS,
    MARKETING_CONSENT_INFORMATION_STATUS,
    PERSONAL_INFORMATION_STATUS,
    POLITICAL_EXPOSURE_INFORMATION_STATUS,
    REGISTRATION_STATUS,
    SUMMARY_STATUS,
    TAX_RESIDENCY_INFORMATION_STATUS,
    UPLOAD_STATUS
} from "../constants/navigationConstants";
import {
    getCountriesById,
    getCountryById,
    getCountryNullableById,
    getSifarnikGotovinskeTransakcijeById,
    getSifarnikIzvoraOstalihPrihodaByIds,
    getSifarnikKanaliDostaveByIds,
    getSifarnikKanalKredPrekById,
    getSifarnikKanalOpciDokById,
    getSifarnikKanalRacKartById,
    getSifarnikOcekivaniPrometById,
    getSifarnikPriljevaSredstavaByIds,
    getSifarnikProsjecanIznosOstalihPrihodaById,
    getSifarnikProsjecnaPrimanjaById,
    getSifarnikStatusZaposlenjaById,
    getSifarnikSvrhaRarcunaByIds,
    getSifarnikVrstaPoslodavcaById,
    getSifarnikVrstaZaposlenjaById,
    getSifarnikVrsteTransakcijaByIds,
    getUkrajinaRegijaById
} from "../../util/sifarnikUtil";
import { GdprCookieInformation } from "../../component/RegistrationPage/CookieConsent";
import { findCookieValueByKey } from "../../util/cookieUtil";
import { MarketingConsentInformation, MarketingConsentInformationCallback } from "../../model/MarketingConsentType";
import { SupportedLanguage } from "../../i18n";
import { useTranslation } from "react-i18next";
import { ContactAddressInformation, initialContactAddressFormValues, DELIVERY_ADDRESS, DeliveryAddress } from "../../model/ContactAddressType";

function mapZahtjevResponseOsobniPodaciToPersonalInformation(input: ZahtjevResponseOsobniPodaci | null | undefined, lng: SupportedLanguage, t: (key: string) => string): PersonalInformation {
    if (!input) {
        return {} as PersonalInformation;
    }

    return {
        spol: input.spol,
        ime: input.ime,
        prezime: input.prezime,
        datumRodjenja: input.datumRodjenja,
        mjestoRodjenja: input.mjestoRodjenja,
        drzavaRodjenja: getCountryById(input.drzavaRodjenja, lng),
        drzavaRodjenjaRegija: input.drzavaRodjenjaRegija ? getUkrajinaRegijaById(input.drzavaRodjenjaRegija, t) : undefined,
        drzavljanstvo: getCountryById(input.drzavljanstvo, lng),
        drzavljanstvoRegija: input.drzavljanstvoRegija ? getUkrajinaRegijaById(input.drzavljanstvoRegija, t) : undefined,
        imaDrugoDrzavljanstvo: input.imaDrugoDrzavljanstvo,
        drzavljanstvoDrugo: getCountryById(input.drzavljanstvoDrugo, lng),
        drzavljanstvoDrugoRegija: input.drzavljanstvoDrugoRegija ? getUkrajinaRegijaById(input.drzavljanstvoDrugoRegija, t) : undefined,
        telefonPredbroj: input.telefonPredbroj,
        telefon: input.telefon,
        mobitelPredbroj: input.mobitelPredbroj,
        mobitel: input.mobitel,
        mailAdresa: input.mailAdresa,
        ulica: input.ulica,
        kucniBr: input.kucniBr,
        mjesto: input.mjesto,
        postanskiBroj: input.postanskiBroj,
        drzava: getCountryById(input.drzava, lng),
        dostavaDrugaAdresa: input.dostavaDrugaAdresa,
        dostavaUlica: input.dostavaUlica,
        dostavaKucniBr: input.dostavaKucniBr,
        dostavaMjesto: input.dostavaMjesto,
        dostavaPostanskiBroj: input.dostavaPostanskiBroj,
        dostavaDrzava: getCountryById(input.dostavaDrzava, lng),
        idDokumentVrsta: input.idDokumentVrsta,
        idDokumentBroj: input.idDokumentBroj,
        idDokumentIzdavatelj: input.idDokumentIzdavatelj,
        idDokumentDrzavaIzdavanja: getCountryById(input.idDokumentDrzavaIzdavanja, lng),
        idDokumentDrzavaIzdavanjaRegija: input.idDokumentDrzavaIzdavanjaRegija ? getUkrajinaRegijaById(input.idDokumentDrzavaIzdavanjaRegija, t) : undefined,
        idDokumentDatumVazenja: input.idDokumentDatumVazenja,
        idDokumentTrajnoVazenje: input.idDokumentTrajnoVazenje,
    }
}

function mapZahtjevResponseDostavaDokumentacijeToDocumentationDeliveryInformation(input: ZahtjevResponseDostavaDokumentacije | null | undefined): DocumentationDeliveryInformation {
    if (!input) {
        return {} as DocumentationDeliveryInformation;
    }

    return {
        kanalRacKart: getSifarnikKanalRacKartById(input.kanalRacKart),
        kanalKredPrek: getSifarnikKanalKredPrekById(input.kanalKredPrek),
        kanalOpciDok: getSifarnikKanalOpciDokById(input.kanalOpciDok),
    }
}

function mapZahtjevResponseMarketinskaPrivolaToMarketingConsentInformation(input: ZahtjevResponseMarketinskaPrivola | null | undefined): MarketingConsentInformation {
    if (!input) {
        return {} as MarketingConsentInformation;
    }

    return {
        privolaMarketinskeInformacije: input.privolaMarketinskeInformacije,
        privolaPersonaliziranePonude: input.privolaPersonaliziranePonude,
        kanaliDostave: getSifarnikKanaliDostaveByIds(input.kanaliDostave ? input.kanaliDostave.split(',') : []),
    }
}

function mapDrzavaOporezivanjaToTaxResidencyCountryInformation(input: DrzavaOporezivanja, lng: SupportedLanguage): TaxResidencyCountryInformation {
    return {
        country: getCountryNullableById(input.drzava, lng)!,
        taxNumber: input.porezniBroj,
    }
}

function mapZahtjevResponsePoreznaRezidentnostToTaxResidencyInformation(input: ZahtjevResponsePoreznaRezidentnost | null | undefined, lng: SupportedLanguage): TaxResidencyInformation {
    if (!input) {
        return {} as TaxResidencyInformation;
    }

    return {
        imaDodatnoAmerickoDrzavljanstvo: input.imaDodatnoAmerickoDrzavljanstvo,
        zelenaKarta: input.zelenaKarta,
        porezniObveznikSAD: input.porezniObveznikSAD,
        posjedujePorezniBrojSAD: input.posjedujePorezniBrojSAD,
        porezniBrojSAD: input.porezniBrojSAD,
        porezniObveznikDrzaveKojaNijeSAD: (input.drzaveOporezivanja?.length ?? 0) > 0,
        drzaveOporezivanja: input.drzaveOporezivanja?.map(drzavaOporezivanja => mapDrzavaOporezivanjaToTaxResidencyCountryInformation(drzavaOporezivanja, lng)) ?? []
    }
}

function mapZahtjevResponseFinancijskeInformacijeToFinancialInformation(input: ZahtjevResponseFinancijskeInformacije | null | undefined, lng: SupportedLanguage): FinancialInformation {
    if (!input) {
        return {} as FinancialInformation;
    }

    return {
        statusZaposlenja: getSifarnikStatusZaposlenjaById(input.statusZaposlenja),
        ...(input.pozicijaZaposlenja && {
            pozicijaZaposlenja: getSifarnikVrstaZaposlenjaById(input.pozicijaZaposlenja),
        }),
        ...(input.vrstaPoslodavca && {
            vrstaPoslodavca: getSifarnikVrstaPoslodavcaById(input.vrstaPoslodavca),
        }),
        prosjecnaMjesecnaPrimanjaRedovna: getSifarnikProsjecnaPrimanjaById(input.prosjecnaMjesecnaPrimanjaRedovna),
        ...(input.prosjecanIznosOstalihPrihoda && {
            prosjecanIznosOstalihPrihoda: getSifarnikProsjecanIznosOstalihPrihodaById(input.prosjecanIznosOstalihPrihoda),
        }),
        ...(input.prosjecneGotovinskeTransakcije && {
            prosjecneGotovinskeTransakcije: getSifarnikGotovinskeTransakcijeById(input.prosjecneGotovinskeTransakcije),
        }),
        ocekivaniGodisnjiPromet: getSifarnikOcekivaniPrometById(input.ocekivaniGodisnjiPromet),
        izvoriOstalihPrihoda: getSifarnikIzvoraOstalihPrihodaByIds(input.izvorOstalihPrihoda.split(',')),
        vrsteTransakcijaNaRacunu: getSifarnikVrsteTransakcijaByIds(input.vrsteTransakcijaNaRacunu.split(',')),
        osnovePriljevaSredstava: getSifarnikPriljevaSredstavaByIds(input.osnovaPriljevaSredstava.split(',')),
        svrheOtvaranjaRacuna: getSifarnikSvrhaRarcunaByIds(input.svrhaOtvaranjaRacuna.split(',')),
        ...(input.drzavePrekogranicnoPlacanje && input.drzavePrekogranicnoPlacanje.length > 0 && {
            drzavePrekogranicnoPlacanje: getCountriesById(input.drzavePrekogranicnoPlacanje.split(','), lng),
        }),
    }
}

type ZahtjevContext = {
    getStatus: () => string;
    restoreContext: (status: string) => void;
    getZahtjevId: () => number;
    oibInformation: OibInformation;
    handleSuccessFullRegistration: (oib: string, response: CreateZahtjevResponse) => void;
    uploadInformation: UploadInformation;
    uploadInformationCallback: (data: UploadInformation) => void;
    personalInformation: PersonalInformation;
    personalInformationCallback: PersonalInformationCallback;
    contactAddressInformation: ContactAddressInformation;
    contactAddressCallback: (data: ContactAddressInformation) => void;
    financialInformation: FinancialInformation;
    financialInformationCallback: FinancialInformationCallback;
    taxResidency: TaxResidencyInformation;
    taxResidencyCallback: TaxResidencyInformationCallback;
    politicalExposure: PoliticalExposureInformation;
    politicalExposureCallback: PoliticalExposureInformationCallback;
    documentationDelivery: DocumentationDeliveryInformation;
    documentationDeliveryCallback: DocumentationDeliveryInformationCallback;
    marketingConsent: MarketingConsentInformation;
    marketingConsentCallback: MarketingConsentInformationCallback;
    clearContext: () => void;
    isProcessCompleted: boolean;
    setIsProcessCompleted: (status: boolean) => void;
    setAnalyticsUserId: (data: string) => void;
    gdprCookieInformation: GdprCookieInformation;
    setGdprCookieData: (data: GdprCookieInformation) => void;
    isCookieConsentSet: () => boolean;
    isScrollToTop: boolean;
    setIsScrollToTop: (isScrollToTop: boolean) => void;
};

export const ZahtjevContext = createContext<ZahtjevContext>({
    getStatus: () => "-",
    restoreContext: () => {
    },
    getZahtjevId: () => -1,
    oibInformation: {} as OibInformation,
    handleSuccessFullRegistration: () => {
    },
    uploadInformation: {} as UploadInformation,
    uploadInformationCallback: () => {
    },
    personalInformation: {} as PersonalInformation,
    personalInformationCallback: {} as PersonalInformationCallback,
    contactAddressInformation: initialContactAddressFormValues,
    contactAddressCallback: () => {
    },
    financialInformation: {} as FinancialInformation,
    financialInformationCallback: {} as FinancialInformationCallback,
    taxResidency: {} as TaxResidencyInformation,
    taxResidencyCallback: {} as TaxResidencyInformationCallback,
    politicalExposure: {} as PoliticalExposureInformation,
    politicalExposureCallback: {} as PoliticalExposureInformationCallback,
    documentationDelivery: {} as DocumentationDeliveryInformation,
    documentationDeliveryCallback: {} as DocumentationDeliveryInformationCallback,
    marketingConsent: {} as MarketingConsentInformation,
    marketingConsentCallback: {} as MarketingConsentInformationCallback,
    clearContext: () => {
    },
    isProcessCompleted: false,
    setIsProcessCompleted: () => {
    },
    setAnalyticsUserId: () => {
    },
    gdprCookieInformation: {} as GdprCookieInformation,
    setGdprCookieData: () => {
    },
    isCookieConsentSet: () => false,
    isScrollToTop: false,
    setIsScrollToTop: () => false,
});

type ZahtjevProviderProps = {
    children: React.ReactNode;
};

export const ZahtjevProvider = ({ children }: ZahtjevProviderProps) => {
    const { t, i18n } = useTranslation();
    const lng = i18n.resolvedLanguage as SupportedLanguage;
    const [oibInformation, oibInformationCallback] = useState<OibInformation | null>(null)
    const [uploadInformation, uploadInformationCallback] = useState<UploadInformation | null>(null);
    const [personalInformation, personalInformationCallback] = useState<PersonalInformation | null>(null);
    const [contactAddressInformation, contactAddressCallback] = useState<ContactAddressInformation>(initialContactAddressFormValues);
    const [financialInformation, financialInformationCallback] = useState<FinancialInformation | null>(null);
    const [taxResidency, taxResidencyCallback] = useState<TaxResidencyInformation | null>(null);
    const [politicalExposure, politicalExposureCallback] = useState<PoliticalExposureInformation | null>(null);
    const [documentationDelivery, documentationDeliveryCallback] = useState<DocumentationDeliveryInformation | null>(null);
    const [marketingConsent, marketingConsentCallback] = useState<MarketingConsentInformation | null>(null);
    const [gdprCookieInformation, setGdprCookieInformation] = useState<GdprCookieInformation | null>(null);
    const [isProcessCompleted, setIsProcessCompleted] = useState<boolean>(false);
    const [isScrollToTop, setIsScrollToTop] = useState<boolean>(false);

    const navigate = useNavigate();
    const { confirm } = useDialog();
    const { notify } = useNotification();

    const getZahtjevId = useCallback(() => {
        const id = sessionStorage.getItem(ZAHTJEV_TOKEN_KEY);
        if (!id) {
            return -1;
        }

        const parsedId = parseInt(id, 10);
        if (isNaN(parsedId)) {
            return -1;
        }
        return parsedId;
    }, []);

    const { refetch: fetchZahtjev } = useGetZahtjev(getZahtjevId());


    const getStatus = useCallback((): string => {
        if (!oibInformation || Object.keys(oibInformation).length === 0) return REGISTRATION_STATUS;
        if (!uploadInformation || Object.keys(uploadInformation).length === 0) return UPLOAD_STATUS;
        if (!personalInformation || Object.keys(personalInformation).length === 0) return PERSONAL_INFORMATION_STATUS;
        if (!contactAddressInformation || Object.keys(contactAddressInformation).length === 0) return CONTACT_ADDRESS_STATUS;
        if (!financialInformation || Object.keys(financialInformation).length === 0) return FINANCIAL_INFORMATION_STATUS;
        if (!taxResidency || Object.keys(taxResidency).length === 0) return TAX_RESIDENCY_INFORMATION_STATUS;
        if (!politicalExposure || Object.keys(politicalExposure).length === 0) return POLITICAL_EXPOSURE_INFORMATION_STATUS;
        if (!documentationDelivery || Object.keys(documentationDelivery).length === 0) return DOCUMENTATION_DELIVERY_INFORMATION_STATUS;
        if (!marketingConsent || Object.keys(marketingConsent).length === 0) return MARKETING_CONSENT_INFORMATION_STATUS;
        return SUMMARY_STATUS;
    }, [oibInformation, uploadInformation, personalInformation, contactAddressInformation, financialInformation, taxResidency, politicalExposure,
        documentationDelivery, marketingConsent, isProcessCompleted]);


    const getNavigationFromStatus = useCallback((currentStatus: string): string => {
        if (currentStatus === "1") return '/registration';
        if (currentStatus === "2") return '/upload';
        if (currentStatus === "3") return '/personal-information';
        if (currentStatus === "4") return '/contact-address';
        if (currentStatus === "5") return '/financial-information';
        if (currentStatus === "6") return '/tax-residency';
        if (currentStatus === "7") return '/political-exposure';
        if (currentStatus === "8") return '/documentation-delivery';
        if (currentStatus === "9") return '/marketing-consent';
        if (currentStatus === "10") return '/summary';
        return '/';
    }, []);


    const restoreContext = useCallback((nextStatus: string) => {
        const zahtjevId = getZahtjevId();
        const currentStatus = getStatus();
        const isContextEmpty = oibInformation === null;
        const isZahtjevIdEmpty = zahtjevId === -1;

        initializeGdprCookieInformation();

        // We don't need to restore context if it's already filled
        if (!isContextEmpty) {
            if (parseInt(nextStatus) <= parseInt(UPLOAD_STATUS) && parseInt(currentStatus) > parseInt(UPLOAD_STATUS)) {
                openClearDataDialog(currentStatus);

                return;
            }

            // if (currentStatus < nextStatus && nextStatus > UPLOAD_STATUS) {
            //     navigate(getNavigationFromStatus(currentStatus));
            //     return;
            // }

            return;
        }

        // We don't restore context on the registration page
        if (nextStatus === REGISTRATION_STATUS) {
            return;
        }

        // We cannot restore context without zahtjevId
        if (isZahtjevIdEmpty) {
            navigate("/");
            return;
        }

        const fetchZahtjevPromise = fetchZahtjev();
        fetchZahtjevPromise.then(response => {
            try {
                const data = response?.data;

                if (data && data.status) {
                    oibInformationCallback({ oib: data.oib } as OibInformation);
                    uploadInformationCallback((parseInt(data.status) >= parseInt(UPLOAD_STATUS))
                      ? { documentType: "osobna" } as UploadInformation
                      : {} as UploadInformation);
                    personalInformationCallback(mapZahtjevResponseOsobniPodaciToPersonalInformation(data.osobniPodaci, lng, t));
                    const osobniPodaci = data.osobniPodaci;
                    taxResidencyCallback(mapZahtjevResponsePoreznaRezidentnostToTaxResidencyInformation(data.poreznaRezidentnost, lng));

                    // Restore contact address data for backward compatibility
                    if (parseInt(data.status) >= 4 && osobniPodaci) {
                        contactAddressCallback({
                            telefonPredbroj: osobniPodaci.telefonPredbroj || "",
                            telefon: osobniPodaci.telefon || "",
                            mobitelPredbroj: osobniPodaci.mobitelPredbroj || "",
                            mobitel: osobniPodaci.mobitel || "",
                            mailAdresa: osobniPodaci.mailAdresa || "",
                            ulica: osobniPodaci.ulica || "",
                            kucniBr: osobniPodaci.kucniBr || "",
                            mjesto: osobniPodaci.mjesto || "",
                            postanskiBroj: osobniPodaci.postanskiBroj || "",
                            drzava: osobniPodaci.drzava ? getCountryById(osobniPodaci.drzava, lng) : undefined,
                            drzavaRegija: osobniPodaci.drzavaRegija ? getUkrajinaRegijaById(osobniPodaci.drzavaRegija, t) : undefined,
                            dostavaDrugaAdresa: osobniPodaci.dostavaDrugaAdresa || false,
                            dostavaUlica: osobniPodaci.dostavaUlica || "",
                            dostavaKucniBr: osobniPodaci.dostavaKucniBr || "",
                            dostavaMjesto: osobniPodaci.dostavaMjesto || "",
                            dostavaPostanskiBroj: osobniPodaci.dostavaPostanskiBroj || "",
                            dostavaDrzava: osobniPodaci.dostavaDrzava ? getCountryById(osobniPodaci.dostavaDrzava, lng) : undefined,
                            dostavaDrzavaRegija: osobniPodaci.dostavaDrzavaRegija ? getUkrajinaRegijaById(osobniPodaci.dostavaDrzavaRegija, t) : undefined,
                            boraviste: osobniPodaci.boraviste || false,
                            boravisteUlica: osobniPodaci.boravisteUlica || "",
                            boravisteKucniBr: osobniPodaci.boravisteKucniBr || "",
                            boravisteMjesto: osobniPodaci.boravisteMjesto || "",
                            boravistePostanskiBroj: osobniPodaci.boravistePostanskiBroj || "",
                            boravisteDrzava: osobniPodaci.boravisteDrzava ? getCountryById(osobniPodaci.boravisteDrzava, lng) : undefined,
                            boravisteDrzavaRegija: osobniPodaci.boravisteDrzavaRegija ? getUkrajinaRegijaById(osobniPodaci.boravisteDrzavaRegija, t) : undefined,
                            trecaAdresa: osobniPodaci.trecaAdresa || false,
                            trecaUlica: osobniPodaci.trecaUlica || "",
                            trecaKucniBr: osobniPodaci.trecaKucniBr || "",
                            trecaMjesto: osobniPodaci.trecaMjesto || "",
                            trecaPostanskiBroj: osobniPodaci.trecaPostanskiBroj || "",
                            trecaDrzava: osobniPodaci.trecaDrzava ? getCountryById(osobniPodaci.trecaDrzava, lng) : undefined,
                            trecaDrzavaRegija: osobniPodaci.trecaDrzavaRegija ? getUkrajinaRegijaById(osobniPodaci.trecaDrzavaRegija, t) : undefined,
                            adresaZaDostavuDokumentacije: (osobniPodaci.adresaZaDostavuDokumentacije as DeliveryAddress) || DELIVERY_ADDRESS.PREBIVALISTE,
                        });
                    }

                    financialInformationCallback(mapZahtjevResponseFinancijskeInformacijeToFinancialInformation(data.financijskiPodaci, lng));
                    taxResidencyCallback(mapZahtjevResponsePoreznaRezidentnostToTaxResidencyInformation(data.poreznaRezidentnost, lng));
                    politicalExposureCallback(data.politickaIzlozenost || {} as PoliticalExposureInformation);
                    documentationDeliveryCallback(mapZahtjevResponseDostavaDokumentacijeToDocumentationDeliveryInformation(data.dostavaDokumentacije));
                    marketingConsentCallback(mapZahtjevResponseMarketinskaPrivolaToMarketingConsentInformation(data.marketinskaPrivola))

                    const currentStatus = data.status;
                    // if (currentStatus < nextStatus) {
                    //     navigate(getNavigationFromStatus(currentStatus));
                    // }
                    if (parseInt(nextStatus) <= parseInt(UPLOAD_STATUS) && parseInt(currentStatus) > parseInt(UPLOAD_STATUS)) {
                        openClearDataDialog(currentStatus);
                    }
                } else {
                    throw Error("No data has been received");
                }
            } catch (error) {
                clearContext();
                notify(ERROR_MESSAGE_NOTIFICATION, "error");
                navigate('/');
            }
        }).catch(() => {
              notify(ERROR_MESSAGE_NOTIFICATION, "error");
              navigate('/');
          }
        );
    }, [
        oibInformation,
        uploadInformation,
        personalInformation,
        financialInformation,
        taxResidency,
        politicalExposure,
        documentationDelivery,
        marketingConsent,
        getZahtjevId,
        navigate,
        notify,
        getStatus,
        getNavigationFromStatus,
        isProcessCompleted,
        oibInformationCallback, uploadInformationCallback, personalInformationCallback, contactAddressCallback, financialInformationCallback,
        taxResidencyCallback, politicalExposureCallback, documentationDeliveryCallback, marketingConsentCallback,
        setIsProcessCompleted,
    ]);


    const handleSuccessFullRegistration = useCallback((oib: string, response: CreateZahtjevResponse) => {
        clearContext();
        oibInformationCallback({ oib: oib } as OibInformation);
        personalInformationCallback({} as PersonalInformation);
        financialInformationCallback({} as FinancialInformation);
        taxResidencyCallback({} as TaxResidencyInformation);
        politicalExposureCallback({} as PoliticalExposureInformation);
        documentationDeliveryCallback({} as DocumentationDeliveryInformation);
        marketingConsentCallback({} as MarketingConsentInformation);
        setIsProcessCompleted(false);

        try {
            sessionStorage.setItem(ACCESS_TOKEN_KEY, response?.accessToken);
            sessionStorage.setItem(ZAHTJEV_TOKEN_KEY, response?.zahtjevId.toString());
            sessionStorage.setItem(UPLOAD_ID_KEY, response?.uploadId.toString());
            sessionStorage.setItem(UPLOAD_HASH_KEY, response?.uploadHashKey);
            sessionStorage.setItem(ANALYTIC_USER_ID, response.analyticsUserId);
        } catch (error) {
            notify(ERROR_MESSAGE_NOTIFICATION, "error");
            navigate("/");
        }
    }, [
        oibInformationCallback, uploadInformationCallback, personalInformationCallback,
        financialInformationCallback, taxResidencyCallback, politicalExposureCallback,
        documentationDeliveryCallback, marketingConsentCallback, navigate, notify, setIsProcessCompleted,
    ]);


    const clearContext = useCallback(() => {
        oibInformationCallback(null);
        uploadInformationCallback(null);
        personalInformationCallback(null);
        financialInformationCallback(null);
        taxResidencyCallback(null);
        politicalExposureCallback(null);
        documentationDeliveryCallback(null);
        marketingConsentCallback(null);

        sessionStorage.removeItem(ACCESS_TOKEN_KEY);
        sessionStorage.removeItem(ZAHTJEV_TOKEN_KEY);
        sessionStorage.removeItem(UPLOAD_ID_KEY)
        sessionStorage.removeItem(UPLOAD_HASH_KEY)
    }, [
        oibInformationCallback, uploadInformationCallback, personalInformationCallback,
        financialInformationCallback, taxResidencyCallback, politicalExposureCallback,
        documentationDeliveryCallback, marketingConsentCallback, setIsProcessCompleted,
    ]);

    const openClearDataDialog = (currentStatus?: string) => {
        (async () => {
            const result = await confirm({
                title: t('common.dialog-confirm-title'),
                content: t('common.dialog-confirm-content'),
                confirmText: t('common.dialog-confirm-yes'),
                cancelText: t('common.dialog-confirm-no')
            });

            if (result) {
                clearContext();
                sessionStorage.clear();
                navigate('/registration');
            } else {
                if (currentStatus) {
                    navigate(getNavigationFromStatus(currentStatus));
                } else {
                    navigate(-1);
                }
            }
        })();
    }

    const initializeGdprCookieInformation = () => {
        const cookieData = findCookieValueByKey(document.cookie, COOKIE_PREFERENCES_KEY);

        if (!cookieData || gdprCookieInformation) {
            return;
        }

        try {
            const cookieConsent = JSON.parse(cookieData);
            if (
              cookieConsent.targeting === undefined ||
              cookieConsent.functional === undefined ||
              cookieConsent.performance === undefined
            ) {
                return;
            }
            const cookieConsentContextData: GdprCookieInformation = {
                areMarketingCookiesAllowed: cookieConsent.targeting,
                areBaseCookiesAllowed: cookieConsent.functional,
                areAnalyticsCookiesAllowed: cookieConsent.performance,
            };
            setGdprCookieInformation(cookieConsentContextData);
        } catch (e) {
            console.error('Could not parse JSON for cookie consent data. Error message:', e);
        }
    };

    const isCookieConsentSet = () => {
        return !!findCookieValueByKey(document.cookie, COOKIE_PREFERENCES_KEY);
    };

    const setAnalyticsUserId = (analyticsUserId: string) => {
        sessionStorage.setItem(ANALYTIC_USER_ID, `${analyticsUserId}`);
    };


    const setGdprCookieData = (cookieConsent: GdprCookieInformation) => {
        const cookieData = {
            functional: cookieConsent.areBaseCookiesAllowed,
            performance: cookieConsent.areAnalyticsCookiesAllowed,
            targeting: cookieConsent.areMarketingCookiesAllowed,
        };

        const expiryDate = new Date();
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);

        document.cookie = `${COOKIE_PREFERENCES_KEY}=${JSON.stringify(cookieData)}; expires=${expiryDate.toUTCString()}; Secure`;

        initializeGdprCookieInformation();
    };

    const value = useMemo<ZahtjevContext>(
      () => ({
          getStatus,
          restoreContext,
          getZahtjevId,
          oibInformation: oibInformation || {} as OibInformation,
          handleSuccessFullRegistration,
          uploadInformation: uploadInformation || {} as UploadInformation,
          uploadInformationCallback,
          personalInformation: personalInformation || {} as PersonalInformation,
          personalInformationCallback,
          contactAddressInformation: contactAddressInformation || initialContactAddressFormValues,
          contactAddressCallback,
          financialInformation: financialInformation || {} as FinancialInformation,
          financialInformationCallback,
          taxResidency: taxResidency || {} as TaxResidencyInformation,
          taxResidencyCallback,
          politicalExposure: politicalExposure || {} as PoliticalExposureInformation,
          politicalExposureCallback,
          documentationDelivery: documentationDelivery || {} as DocumentationDeliveryInformation,
          documentationDeliveryCallback,
          marketingConsent: marketingConsent || {} as MarketingConsentInformation,
          marketingConsentCallback,
          clearContext,
          isProcessCompleted,
          setIsProcessCompleted,
          setAnalyticsUserId,
          setGdprCookieData,
          gdprCookieInformation: gdprCookieInformation || {} as GdprCookieInformation,
          isCookieConsentSet,
          isScrollToTop,
          setIsScrollToTop,
      }),
      [
          oibInformation, uploadInformation, personalInformation, financialInformation, taxResidency,
          politicalExposure, documentationDelivery, restoreContext, getZahtjevId, handleSuccessFullRegistration,
          uploadInformationCallback, personalInformationCallback, contactAddressCallback, financialInformationCallback, taxResidencyCallback,
          politicalExposureCallback, documentationDeliveryCallback, documentationDeliveryCallback, clearContext,
          getStatus, isProcessCompleted, setIsProcessCompleted, setAnalyticsUserId, gdprCookieInformation,
          setGdprCookieData, isCookieConsentSet, isScrollToTop, setIsScrollToTop,
      ]
    );

    return <ZahtjevContext.Provider value={value}>{children}</ZahtjevContext.Provider>;
};
